import React, {
  Fragment,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import Scrollbar from 'perfect-scrollbar';
import { Elements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FiArrowLeft } from 'react-icons/fi';
import moment from 'moment-timezone';
import { sanitizeTranslation } from 'library/helpers/translationHelpers';
import { AuthContext } from 'context/AuthProvider';
import { GlobalContext } from 'context/GlobalProvider';
import { ReservationContext } from 'context/ReservationProvider';
import { PopeyeContext } from 'context/PopeyeProvider';

import Spinner from 'components/Spinner/Spinner';
import Loader from 'components/Loader/Loader';
import Input from 'components/UI/Antd/Input/Input';
import Select from 'components/UI/Antd/Select/Select';
import Row from 'components/UI/Antd/Grid/Row';
import Col from 'components/UI/Antd/Grid/Col';
import Tabs from 'components/UI/Antd/Tabs/Tabs';
import Collapse from 'components/UI/Antd/Collapse/Collapse';
import Drawer from 'components/UI/Antd/Drawer/Drawer';
import ConfirmModal from 'components/UI/ConfirmModal/ConfirmModal';
import ResultDialog from 'components/UI/ResultDialog/ResultDialog';
import Container from 'components/UI/Container/Container';
import Button from 'components/UI/Antd/Button/Button';
import { Button as StyledButton } from 'components/common/Button';

import ReservationDetails from './ReservationDetails';
import Submission, { groups } from './Submission/Submission';
import Messages from './Messages';
import CardForm from './Payment/CardForm';
import CardAction from './Payment/CardAction';
import NotificationAlert from './NotificationAlert/NotificationAlert';

import { NOT_FOUND_PAGE, POPEYE_SUCCESS_SCREEN } from 'settings/constant';

import useWindowSize from 'library/hooks/useWindowSize';
import { scrollToErrorField, generateID } from 'library/helpers/utils';
import {
  updateUserProperties,
  getFormattedReservationEvent
} from 'library/helpers/amplitude';
import ReactPixel from 'services/facebookPixel';
import ReservationWrapper, {
  SidebarScrollOuter,
  SidebarScrollInner,
  Sidebar,
  ReasonField,
  ReservationActions,
  ReservationDetailsMobileWrapper,
  GroupTitle,
  SectionHeader,
  CollapseWrapper,
  PaymentSectionWrapper,
  MessagingHeader,
  BackButtonTitle,
  PaymentDetailsTitle,
  PaymentDetailsAlert,
  ActionSection,
  ActionTitle,
  Actions,
  Divider,
  ContactDetails,
  ContactInfo,
  ContactInfoTitle,
  ContactDetail, GroupNotice,
  CANCELLATION_POLICY
} from './Reservation.style';

import { message } from 'antd';
import { getStateFromUrl } from 'library/helpers/url_handler';
import useAmplitude from 'library/hooks/useAmplitude';
import { useSessionStorage } from 'library/hooks/useSessionStorage';
import { PhoneFilled, MailFilled  } from '@ant-design/icons';
import { SuccessDialogActions } from './Submission/Submission.style';
import ButtonWithAsync from 'components/UI/ButtonWithAsync/ButtonWithAsync';
import {formatPrice} from "../../../utils/format";
import attachmentService from 'services/attachment';

const AsyncButton = ButtonWithAsync(Button);

const SUBMISSION_TAB_KEY = 'submission';
const CHAT_TAB_KEY = 'chat';

message.config({
  top: 100,
  duration: 7,
  maxCount: 1
});
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY);

const { Panel } = Collapse;
const { TabPane } = Tabs;
const { Option } = Select;

const cancellationOptions = [
  'BOATER_RESERVATION_CANCELATION_REASON_MISTAKE',
  'BOATER_RESERVATION_CANCELATION_REASON_BETTER_DEAL',
  'BOATER_RESERVATION_CANCELATION_REASON_CANT_MAKE_IT',
  'BOATER_RESERVATION_CANCELATION_REASON_CANT_NO_RESPONSE',
  'BOATER_RESERVATION_CANCELATION_REASON_CANCEL',
  'OTHER'
];

const stripeElementsOptions = user => ({
  fonts: [ { cssSrc: 'https://fonts.googleapis.com/css?family=Lato:300,300i,400,500,600' } ],
  locale: user.language || 'en'
});

export const docTypes = {
  vessel_insurance: 'VESSEL_INSURANCE',
  vessel_license: 'VESSEL_LICENSE',
  skipper_id: 'SKIPPER_ID_LICENSE',
  vessel_photo: 'VESSEL_PHOTO'
};

const UPDATE_DATE_FORMAT = 'MMM DD, HH:mm:ss';
const marinaLogos = (marina_id) => {
  return ((process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina_id === 82)
  || (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina_id === 411)||
  (process.env.REACT_APP_FIREBASE_ENV === 'DEVELOPMENT' && marina_id === 7))
}

const RenderReservationPage = props => {
  const { t } = useTranslation();
  const { sendToAmplitude } = useAmplitude();
  const { width } = useWindowSize();
  const [numberOfMarinasViewed, setNumberOfMarinasViewed] = useSessionStorage('numberOfMarinasViewed',[]);

  const requirementsFormRef = useRef();
  const filledRequirementsFormRef = useRef();

  const { user, updates, refreshData, getStatisticsForBoater } = useContext(AuthContext);
  const { loading, getSingleMarina, loadAllMarinas } = useContext(GlobalContext);
  const { isPopeyeMember, isPreSignatureStatus } = useContext(PopeyeContext);
  const {
    reservation,
    getReservationAndMessages,
    readMessages,
    unread,
    clearReservationContext,
    updateReservation,
    resubmitReservation,
    onOpenARBanner,
    usePopeyeBalance
  } = useContext(ReservationContext);

  const [isFetching, setIsFetching] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [marina, setMarina] = useState({});
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [cancelFinished, setCancelFinished] = useState(false);
  const [beforeConfirmOpened, setBeforeConfirmOpened] = useState(false);
  const [acceptDisabled, setAcceptDisabled] = useState(false);
  const [reason, setReason] = useState('');
  const [isOtherReasonSelected, setIsOtherReasonSelected] = useState(false);
  const [reasonDescription, setReasonDescription] = useState('');
  const [activeTab, setActiveTab] = useState(SUBMISSION_TAB_KEY);
  const [reservationUpdateData, setReservationUpdateData] = useState({});
  const [personalMissingRequirements, setPersonalMissingRequirements] = useState([]);
  const [vesselMissingRequirements, setVesselMissingRequirements] = useState([]);
  const [documentsMissingRequirements, setDocumentsMissingRequirements] = useState([]);
  const [tripInfoMissingRequirements, setTripInfoMissingRequirements] = useState([]);
  const [emptyReasonError, setEmptyReasonError] = useState('');
  const [infoModalOpened, setInfoModalOpened] = useState(false);
  const [messageDrawerOpened, setMessageDrawerOpened] = useState(false);
  const [isZeroPrice, setIsZeroPrice] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [lastStatus, setLastStatus] = useState(null);
  const [expired, setExpired] = useState(false);
  const [dateLeft, setDateLeft] = useState(null);
  const [enablePaymentButton, setEnablePaymentButton] = useState(false);
  const [showCardError, setShowCardError] = useState(false);
  const [error, setError] = useState(null);

  const phone = marina && !isEmpty(marina.country_code) ? marina.country_code + marina.phone : marina.phone;

  const [cancellationPolicy, setCancellationPolicy] = useState(null);

  useEffect(() => {
    const fetchAttachment = async () => {
      if(marina?.id && reservation){
          try {
          const requested_type = reservation?.contract_name ? 'CANCELLATION_POLICY_LONG' : 'CANCELLATION_POLICY_SHORT';
          const attachment = await attachmentService.getAttachmentByType({ requested_type, marina_id: marina.id });
          setCancellationPolicy(attachment && attachment[0]);
        } catch (error) {
          console.error('Error fetching attachment:', error);
        }
      }
 
    };
  
    fetchAttachment();
  }, [reservation, marina]);

  const reservationUpdate = updates.reservation
    ? updates.reservation.format(UPDATE_DATE_FORMAT)
    : null;
  const messageUpdate = updates.message
    ? updates.message.format(UPDATE_DATE_FORMAT)
    : null;

  const sendAmplitudeUpdateReservationEvent = ({
    data,
    unmet_required_fields,
    type
  }) => {
    const reservationVessel = user.vessels.filter(
      ({ id }) => id === reservation.vessel_id
    )[0];

    const today = moment().startOf('day');
    const days_before = moment(reservation.departure_date).diff(today, 'days');

    const eventProperties = getFormattedReservationEvent({
      reservationVessel,
      data,
      payment,
      user,
      reservation,
      marina,
      location: props.location,
      days_before,
      unmet_required_fields,
      marinas_viewed_before: numberOfMarinasViewed.length - 1,
      type,
      popeye_discount: reservation.popeye_discount
    });

    sendToAmplitude({
      eventProperties,
      eventName: 'reservation_updated'
    });
    setNumberOfMarinasViewed([]);
  };

  const fetchReservationAndMessages = async (id, user) => {
    if (isFetching) {
      return false;
    }
    setIsFetching(true);
    const res = await getReservationAndMessages(id);
    if (!res) {
      props.history.push(`/${props.match.params.locale}${NOT_FOUND_PAGE}`);
      setIsFetching(false);
      return false;
    }
    const { attachments, marina_id, vessel_id } = res;

    const resMarina = getSingleMarina(marina_id);
    const { marina_settings } = resMarina;
    const { marina_requirements } = marina_settings;
    let marinaRequirements = marina_requirements[reservation?.contract_id ? 'long' : 'short'].after_approval;

    //in order to show them in the page
    marinaRequirements.push('first_name');
    marinaRequirements.push('last_name');
    marinaRequirements.push('email_address');
    marinaRequirements.push('vessel_draft');
    marinaRequirements.push('vessel_beam');
    marinaRequirements.push('vessel_loa');
    marinaRequirements.push('vessel_name');
    setMarina(resMarina);

    const { vessels } = user;
    const currentReservationBoaterVessel = vessels.filter(
      ({ id }) => id === vessel_id
    )[0];
    const {
      name: vessel_name,
      home_marina_name,
      water_electricity,
      type: vessel_type,
      beam: vessel_beam,
      draft: vessel_draft,
      length: vessel_loa,
      hull_type: vessel_hull_type
    } = currentReservationBoaterVessel;
    const adaptedVesselObj = {
      vessel_name,
      home_marina_name,
      water_electricity,
      vessel_type,
      vessel_draft,
      vessel_beam,
      vessel_loa,
      vessel_hull_type
    };
    const adapledUserObj = { ...user, email_address: user.email };

    const personalMissings = groups.personal_info.fields.filter(field => {
      /**
       * The address field combined by one requirement from marina - 'address'
       * and has two fields
       */
      const isAddressField =
        field === 'address_line_two' || field === 'address';
      const formattedField = isAddressField ? 'address' : field;
      return (
        marinaRequirements.some(req => {
          return req === formattedField;
        }) && !adapledUserObj[formattedField]
      );
    });
    const vesselMissings = groups.vessel_info.fields.filter(
      field =>
        marinaRequirements.some(req => req === field) &&
        (adaptedVesselObj[field] === null ||
          adaptedVesselObj[field] === '' ||
          adaptedVesselObj[field] === undefined)
    );
    const documentsMissing = groups.documents.fields.filter(
      field =>
        marinaRequirements.some(req => req === field) &&
        !attachments.some(resAtt => resAtt.attachment_type === docTypes[field])
    );

    const tripMissing = groups.trip_info.fields.filter(
      field =>
      marinaRequirements.some( req => req === field ) &&
      (res[field] === null)
    )

    setPersonalMissingRequirements(personalMissings);
    setVesselMissingRequirements(vesselMissings);
    setDocumentsMissingRequirements(documentsMissing);
    setTripInfoMissingRequirements(tripMissing)

    // last status
    const filteredStatues = res.statuses.filter(
      status => status.status_code !== 'BOATER_ADDED_DOCUMENTS'
    );
    const lastStat = filteredStatues[filteredStatues.length - 1];
    setLastStatus(lastStat);
    const { action_required: lastActionRequired } = lastStat;
    const marina_timezone = resMarina ? resMarina.timezone : null;
    const dayOfReservationNoonMarinaTime = moment.tz(res.start_date, marina_timezone).startOf('day').add(12, 'hours');
    const dayOfReservationMidnightMarinaTime = moment.tz(res.start_date, marina_timezone).startOf('day').add(24, 'hours');

    if (
      lastActionRequired === 'BOATER' &&
      lastStat.status_code === 'UPDATED' &&
      lastStat.fields_updated.includes('payment')
    ) {
      const lastStatus = moment.tz(lastStat.updated_at, marina_timezone);
      const expirationAfter24Hours = lastStatus.clone().add(1, 'days');

      if (expirationAfter24Hours.isAfter(dayOfReservationNoonMarinaTime)) {
        if (lastStatus.isBefore(dayOfReservationNoonMarinaTime)) {
          setDateLeft(dayOfReservationNoonMarinaTime);
        } else if (lastStatus.isSameOrAfter(dayOfReservationNoonMarinaTime) &&
                  lastStatus.isBefore(dayOfReservationMidnightMarinaTime)) {
          setDateLeft(dayOfReservationMidnightMarinaTime);
        }
      } else {
        setDateLeft(expirationAfter24Hours);
      }
    }
    // expired date time set
    if (
      lastActionRequired === 'BOATER' &&
      lastStat.status_code === 'PAYMENT_EXPIRED'
    ) {
      setExpired(true);
    } else {
      setExpired(false);
    }

    setLoaded(true);
    setAcceptDisabled(false);
    setIsFetching(false);
  };

  useEffect(() => {
    if (!loading && (reservationUpdate || messageUpdate)) {
      fetchReservationAndMessages(props.match.params.id, user);
    }
  }, [loading, reservationUpdate, messageUpdate, props.match.params.id, user]);

  useEffect(() => {
    return () => {
      readMessages();
    };
  }, [unread]);

  useEffect(() => {
    return () => {
      clearReservationContext();
    };
  }, []);

  useEffect(() => {
    if (lastStatus) {
      const { action_required: lastActionRequired } = lastStatus;
      const searchParams = getStateFromUrl(props.location);

      if (searchParams && searchParams.reservation_tab) {
        if (searchParams.reservation_tab === CHAT_TAB_KEY) {
          if (width < 768) {
            setMessageDrawerOpened(true);
          } else {
            setActiveTab(CHAT_TAB_KEY);
          }
        } else {
          setActiveTab(searchParams.reservation_tab);
        }
      } else if (lastActionRequired === 'FINISHED') {
        setActiveTab(CHAT_TAB_KEY);
      }
    }
  }, [props.location, lastStatus]);

  useEffect(() => {
    if (!loaded || loading || width < 768 || isEmpty(marina)) {
      return;
    }
    const cont = document.getElementById('asideScrollContainer');
    let ps = new Scrollbar(cont);

    return () => {
      ps && ps.destroy();
      ps = null;
    };
  }, [loaded, loading, width, marina]);

  const defaultActiveKey = useMemo(() => {
    if (isEmpty(marina)) return ['1', '2'];
    const isSmthMissing =
      !isEmpty(personalMissingRequirements) ||
      !isEmpty(vesselMissingRequirements) ||
      !isEmpty(documentsMissingRequirements)||
      !isEmpty(tripInfoMissingRequirements);
    return isSmthMissing ? ['1', '2'] : ['2'];
  }, [
    marina,
    personalMissingRequirements,
    vesselMissingRequirements,
    documentsMissingRequirements,
    tripInfoMissingRequirements
  ]);

  if (!loaded || loading || isEmpty(marina)) {
    return <Loader />;
  }

  const { payment, payment_accepted_online } = reservation;
  const { action_required, status_code } = lastStatus;
  const missingRequirementsExist = Boolean(
    personalMissingRequirements
      .concat(vesselMissingRequirements)
      .concat(documentsMissingRequirements)
      .concat(tripInfoMissingRequirements).length
  );

  const onCloseafterCancel=()=>{
    setCancelFinished(false);
  }

  const onTabChange = key => {
    setActiveTab(key);
    window.scrollTo(0, 0);
    if (key === SUBMISSION_TAB_KEY) {
      const searchParams = getStateFromUrl(props.location);
      if (
        searchParams.reservation_tab &&
        searchParams.reservation_tab === CHAT_TAB_KEY
      ) {
        props.history.push(props.location.pathname);
      }
    }
  };

  const openConfirmModal = () => {
    setConfirmOpened(true);
  };

  const onUpdateStatistic = async () => {
    const boater = await refreshData({
      parameters: ['vessels', 'boater', 'marina_requests']
    });
    getStatisticsForBoater().then(statistics =>
      updateUserProperties({ boater, statistics })
    );
  };

  const onCloseMessageDrawer = () => {
    setMessageDrawerOpened(false);
    const searchParams = getStateFromUrl(props.location);
    if (
      searchParams.reservation_tab &&
      searchParams.reservation_tab === CHAT_TAB_KEY
    ) {
      props.history.push(props.location.pathname);
    }
  };

  // ---------------------- //
  // SUBMIT HANDLERS
  // ---------------------- //

  const onCancelReservation = async () => {
    const isDescriptionEmpty = isOtherReasonSelected && !reasonDescription;
    const declineReason = isOtherReasonSelected
      ? reasonDescription
      : reason.trim();
    if (!reason || isDescriptionEmpty) {
      return setEmptyReasonError(t('PLEASE_PROVIDE_REASON'));
    }
    const data = {
      reservation_id: props.match.params.id,
      method: 'CANCEL',
      vessel: { id: reservation.vessel.id }
    };
    await updateReservation({ ...data, decline_reason: declineReason });
    if (reason.trim().length) {
      setReason('');
      setReasonDescription('');
      setEmptyReasonError('');
    }
    setConfirmOpened(false);
    setCancelFinished(true);


    // for amplitude purpose.
    onUpdateStatistic();
  };

  const onUpdateReservationHandler = async (data, payWithStripe) => {
    // For amplitude purpose
    let type = data.method === 'UPDATE' ? 'updated_fields' : 'booked';
    let unmet_required_fields = [];
    let res;

    if (
      isArray(marina.marina_settings.marina_requirements.short.after_approval) &&
      marina.marina_settings.marina_requirements.short.after_approval.length
    ) {
      if (missingRequirementsExist) {
        if (activeTab !== SUBMISSION_TAB_KEY) {
          setActiveTab(SUBMISSION_TAB_KEY);
        }
        if (requirementsFormRef.current) {
          setReservationUpdateData({ ...data, payWithStripe });
          requirementsFormRef.current.handleSubmit();
        }
        unmet_required_fields = [
          ...personalMissingRequirements,
          ...vesselMissingRequirements,
          ...documentsMissingRequirements,
          ...tripInfoMissingRequirements
        ];
      } else {
        setAcceptDisabled(true);
        setShowSpinner(true);

        if (filledRequirementsFormRef.current) {
          await filledRequirementsFormRef.current.handleSubmit();
        }
        res = await updateReservation(data);
        if (res) {
          const { error_type } = res;
          if (!error_type) {
            if (!isZeroPrice && payment_accepted_online) {
              const result = await payWithStripe(usePopeyeBalance);
              if (result.error) {
                res.isPaymentDeclined = true;
                setAcceptDisabled(false);
              }
              // returns the response of updatePayment route
              else {
                res.updatedPayment = result;
                onUpdateStatistic();
                sendAmplitudeUpdateReservationEvent({
                  data,
                  unmet_required_fields,
                  type
                });
              }
            }
          } else if (error_type === 'CONFLICT') {
            setInfoModalOpened(true);
            setAcceptDisabled(false);
            setShowSpinner(false);
            return false;
          }
        }

        setShowSpinner(false);
      }
    } else {
      setShowSpinner(true);
      res = await updateReservation(data);
      if (res && !res.error_type) {
        if (!isZeroPrice && payment_accepted_online) {
          const result = await payWithStripe(usePopeyeBalance);
          if (result.error) res.isPaymentDeclined = true;
          // returns the response of updatePayment route
          else res.updatedPayment = result;
        }

        onUpdateStatistic();
        sendAmplitudeUpdateReservationEvent({
          data,
          unmet_required_fields,
          type
        });
      }
      setShowSpinner(false);
    }

    return res;
  };

  const submitRequiredFieldsCB = async data => {
    setAcceptDisabled(true);
    setShowSpinner(true);

    if (filledRequirementsFormRef.current) {
      await filledRequirementsFormRef.current.handleSubmit();
    }

    const requestData = { ...reservationUpdateData, ...data };
    const res = await updateReservation(requestData);
    requestData.method === 'ACCEPT' && sendAmplitudeUpdateReservationEvent({
      data,
      type: 'booked'
    });
    if (res) {
      const { error_type } = res;
      if (!error_type) {
        if (
          !isZeroPrice &&
          payment_accepted_online &&
          reservationUpdateData.payWithStripe
        ) {
          const result = await reservationUpdateData.payWithStripe(
            usePopeyeBalance
          );
        }

      } else if (error_type === 'CONFLICT') {
        setInfoModalOpened(true);
        setAcceptDisabled(false);
        setShowSpinner(false);
        return false;
      }
    }

    await refreshData({ parameters: ['vessels', 'boater'] });

    //Amplitude event for hmv_requested
    if (data.vessel && data.vessel.home_marina_id) {
      const eventProperties = {
        marina_name: marina.name,
        marina_country: marina.country,
        source: 'reservation'
      };

      sendToAmplitude({
        eventProperties,
        eventName: 'hmv_requested'
      });
    }
    //Amplitude event for update reservation

    const reservationVessel = user.vessels.filter(
      ({ id }) => id === reservation.vessel_id
    )[0];

    const today = moment().startOf('day');
    const days_before = moment(reservation.departure_date).diff(today, 'days');

    const eventProperties = getFormattedReservationEvent({
      reservationVessel,
      data: requestData,
      payment,
      user,
      reservation,
      marina,
      location: props.location,
      type: 'updated_fields',
      marinas_viewed_before: numberOfMarinasViewed.length - 1,
      days_before,
      popeye_discount: reservation.popeye_discount
    });

    sendToAmplitude({
      eventProperties,
      eventName: 'reservation_updated'
    });
    setNumberOfMarinasViewed([]);
    setShowSpinner(false);
  };

  const onUpdateReservation = async () => {
    if (acceptDisabled) {
      return false;
    }
    const data = {
      reservation_id: props.match.params.id,
      method: 'UPDATE',
      vessel: { id: reservation.vessel.id }
    };
    await onUpdateReservationHandler(data);
  };

  const onConfirmReservation = async (payWithStripe, enablePaymentButton) => {
    console.log({
      acceptDisabled,
      payment_accepted_online,
      NotEnabled: !enablePaymentButton
    });
    if (
      acceptDisabled ||
      (payment_accepted_online && !enablePaymentButton && !isZeroPrice)
    ) {
      return false;
    }

    const data = {
      reservation_id: props.match.params.id,
      method: 'ACCEPT',
      vessel: { id: reservation.vessel.id }
    };

    /**
     * If there is a balance for Popeye - use it.
     */
    if (usePopeyeBalance) data.use_popeye_balance = true;
    if (isZeroPrice) data.is_zero_reservation_cost = true;

    try {
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        ecommerce: {
          purchase: {
            actionField: {
              id: props.match.params.id // Transaction ID. Required for purchases and refunds.
            },
            products: [
              {
                name: marina.name,
                price: reservation.payment.total_to_pay
              }
            ],
            marina_name: marina.name || '-',
            start_date: reservation.start_date,
            end_date: reservation.end_date,
            country: marina.country || '-',
            hull_type: reservation.vessel.hull_type || '-',
            vessel_loa: reservation.vessel.length || '-',
            vessel_beam: reservation.vessel.beam || '-',
            vessel_draft: reservation.vessel.draft || '-',
            number_of_nights:
              moment(reservation.end_date).diff(
                moment(reservation.start_date),
                'days'
              ) || '-',
            price: reservation.payment.total_to_pay
          }
        },
        eventCallback: function () {
          // document.location = 'checkout.html';
        }
      });

      ReactPixel.track('Purchase', {
        currency: marina.marina_settings.currency,
        value: reservation.payment.total_to_pay
      });
    } catch (e) {
      console.error('cannot submit to datalayer', e);
    }
    const res = await onUpdateReservationHandler(data, payWithStripe);

    const { auto_absence_report } = user;
    const { home_marina_name } = reservation.vessel;
    const hasHomeMarina = !isNil(home_marina_name);

    if (!auto_absence_report && hasHomeMarina) onOpenARBanner();

    if (
      res &&
      res.updatedPayment &&
      res.updatedPayment.popeye &&
      (isPopeyeMember || isPreSignatureStatus)
    ) {
      const { number_of_points, have_reported_vacancy, reservation_worth_with_currency, number_of_nights, days_in_advance } =
        res.updatedPayment.popeye;

      // Don't go to success screen if no points earned or if payment declined
      if (!number_of_points || res.isPaymentDeclined) return;

      const popeyeSuccessScreen = {
        type: 'reservation',
        have_reported_vacancy,
        host_marina_name: marina.name,
        home_marina_name: have_reported_vacancy && home_marina_name,
        number_of_points,
        days_in_advance,
        reservation_worth_with_currency,
        number_of_nights,
        reservation_id: props.match.params.id
      };

      return props.history.push({
        pathname: `/${props.match.params.locale}${POPEYE_SUCCESS_SCREEN}`,
        state: { successScreen: popeyeSuccessScreen },
        search: `?reservation_id=${data.reservation_id}`
      });
    }
  };

  const onResubmitReservation = async () => {
    const data = { reservation_id: props.match.params.id };
    await resubmitReservation(data);
    await fetchReservationAndMessages(props.match.params.id, user);
  };

  const updateReservationDatesHandler = async (data, callback) => {
    setShowSpinner(true);
    await updateReservation({
      ...data,
      method: 'UPDATE_DATE',
      reservation_id: props.match.params.id,
    });
    await fetchReservationAndMessages(props.match.params.id, user);
    setShowSpinner(false);
    if(callback) {
      sendToAmplitude({
        eventProperties: { type: 'dates' },
        eventName:       'reservation_updated'
      });
      callback();
    }
  }

  // let scrollOuterClass = '';
  // if(status_code === 'INITIATED' && missingRequirementsExist || status_code === 'UPDATED' && !payment_accepted_online) {
  // 	scrollOuterClass = 'with-actions';
  // 	if(status_code === 'UPDATED') {
  // 		scrollOuterClass = scrollOuterClass + ' with-payment';
  // 	}
  // }

  // ---------------------- //
  // RENDER HELPERS
  // ---------------------- //

  const renderTabBar = (props, DefaultTabBar) => (
    <Fragment>
      <DefaultTabBar
        {...props}
        className={
          activeTab === CHAT_TAB_KEY
            ? 'reservation-tab-bar chat-active'
            : 'reservation-tab-bar'
        }
      />
    </Fragment>
  );

  const renderReservationActions = () => {
    if (expired) {
      return (
        <ReservationActions>
          <StyledButton
            fillWidth
            disabled={acceptDisabled}
            label={t('RESERVATION_PAYMENT_RECHECK_AVAILABILITY_BUTTON')}
            onClick={onResubmitReservation}
            onMouseUp={() => scrollToErrorField(true)}
          />
        </ReservationActions>
      );
    } else if ((status_code === 'INITIATED' || status_code === 'MARINA_ADDED_DATES' || status_code === 'BOATER_ACCEPTED_SUGGESTED_DATES') && missingRequirementsExist) {
      return (
        <ReservationActions>
          <StyledButton
            fillWidth
            disabled={acceptDisabled}
            label={t('UPDATE_RESERVATION')}
            onClick={onUpdateReservation}
            onMouseUp={() => scrollToErrorField(true)}
          />
        </ReservationActions>
      );
    } else if (
      status_code === 'UPDATED' &&
      action_required === 'BOATER' &&
      (!payment_accepted_online || isZeroPrice)
    ) {
      return (
        <ReservationActions>
          <StyledButton
            fillWidth
            data-test-id='test-confirm-reservation'
            disabled={acceptDisabled}
            label={t('RESERVATION_CONFIRM_RESERVATION')}
            onClick={onConfirmReservation}
            onMouseUp={() => scrollToErrorField(true)}
          />
        </ReservationActions>
      );
    } else {
      if(status_code === 'MARINA_ADDED_DATES') {
        return null;
      } else {
        return <Divider className='actions-divider' />;
      }
    }
  };

  const renderPaymentActions = () => {
    const intent = reservation.payment
      ? reservation.payment.stripe_client_secret
      : null;

    const attrs = {
      user,
      intent,
      reservation,
      acceptDisabled,
      enablePaymentButton,
      setEnablePaymentButton,
      showCardError,
      setShowCardError,
      error,
      setError,
      onConfirmReservation,
      onMouseUp: () => scrollToErrorField(true),
      CardElement
    }

    return <CardAction {...attrs}/>;
  }

  const renderSubmission = inCollapse => {
    const attrs = {
      vessel_id: reservation?.vessel?.id,
      marina,
      user,
      reservation,
      action_required,
      status_code,
      personalEmptyGroup: personalMissingRequirements,
      vesselEmptyGroup: vesselMissingRequirements,
      documentsEmptyGroup: documentsMissingRequirements,
      tripInfo:tripInfoMissingRequirements,
      requirementsFormRef,
      filledRequirementsFormRef,
      reservation_id: props.match.params.id,
      updateReservation,
      submitRequiredFieldsCB,
      refreshData,
      location: props.location,
      inCollapse
    };
    return <Submission {...attrs} />;
  };

  const renderReservationDetails = () => {
    let suggestions = [];
    const { suggested_dates } = lastStatus;

    const marinaTimeZone = marina?.timezone;

    const canCancelReservation = (startDate) => {
      const startOfReservation = moment.tz(startDate, marinaTimeZone);
      const noonOnStartDate = startOfReservation.clone().set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
      const nowInMarinaTimeZone = moment.tz(marinaTimeZone);
      return nowInMarinaTimeZone.isBefore(noonOnStartDate);
    };

    if(suggested_dates && suggested_dates.length) {
      suggestions = suggested_dates.map(({ start_date, end_date }) => ({
        id: generateID(), start_date, end_date
      }));
    }

    const attrs = {
      marina,
      user,
      onOpenMessageDrawer: () => setMessageDrawerOpened(true),
      action_required,
      status_code,
      expired,
      requested_redeem_nights_amount:
        reservation.requested_redeem_nights_amount,
      calculatedPrice: reservation.pricing_plan,
      pickapierDiscount: reservation.pickapier_discount,
      setIsZeroPrice,
      renderPaymentActions,
      renderReservationActions,
      isZeroPrice,
      updateReservationDatesHandler,
      suggestions,
      source: reservation.source,
      cancellationPolicy
    };
    return (
    <div>
      <ReservationDetails {...attrs} />
      <div className='cancelReservationButton'>
        {status_code !== 'CANCELED_BY_BOATER' && status_code !== 'CANCELED_BY_MARINA' && (
        <StyledButton
          medium
          variant='link'
          disabled={ !canCancelReservation(reservation.start_date)}
          label={t('CANCEL_RESERVATION')}
          onClick={() => (action_required === 'FINISHED')? setBeforeConfirmOpened(true) : setConfirmOpened(true)}
        />
        )}
      </div>
    </div>
    );
  };

  const renderMessages = () => {
    const attrs = {
      user,
      reservation,
      marina,
      id: props.match.params.id,
      isAllowed: Boolean(
        marina.marina_settings.permissions.chat &&
          !reservation.is_handled_by_support
      ),
      isReservation: true,
      isMissingRequirements: missingRequirementsExist
    };
    return <Messages {...attrs} />;
  };

  const renderPaymentForm = () => {
    const attrs = {
      reservation,
      paid: false,
      onConfirmReservation,
      enablePaymentButton,
      setEnablePaymentButton,
      showCardError,
      setShowCardError,
      error,
      setError,
      CardElement,
      marina_id: marina.id
    };

    const hasDeposit = (reservation?.payment?.current_pricing_plan?.deposit ) &&
          (reservation.payment?.current_pricing_plan?.deposit !== reservation.payment?.current_pricing_plan?.total_to_pay );
    const deposit_amount = hasDeposit ?
        ( reservation?.payment?.current_pricing_plan?.deposit ) : undefined;

    return (
      <PaymentSectionWrapper>
        <GroupTitle>
          {t('RESERVATION_PAYMENT_INFO_CREDIT_CARD_TITLE')}
        </GroupTitle>
        { deposit_amount && <GroupNotice>
          {
            t('DEPOSIT_PAYMENT_NOTICE', {
              deposit_amount: formatPrice(
                  deposit_amount,
                  user.language || 'en',
                  reservation.payment?.currency || 'EUR'
              )
          }
        )}</GroupNotice> }
        <CardForm {...attrs} />
      </PaymentSectionWrapper>
    );
  };

  const renderPaymentDetails = () => {
    if (expired) {
      return null;
    }
    const attrs = {
      cardNumber: 'TODO cardnumber',
      payedAt: 'TODO payedAt',
      currency: marina.marina_settings.currency,
      paid: true,
      reservation,
      enablePaymentButton,
      setEnablePaymentButton,
      showCardError,
      setShowCardError,
      error,
      setError
    };
    if (payment_accepted_online && !isZeroPrice) {
      if (!isEmpty(payment) && reservation.payment.transaction_id) {
        return (
          <PaymentSectionWrapper className='paid'>
            <GroupTitle>
              {t('RESERVATION_APPROVED_PAYMENT_DETAILS_TITLE')}
            </GroupTitle>
            <Elements
              options={stripeElementsOptions(user)}
              stripe={stripePromise}
            >
              <CardForm {...attrs} />
            </Elements>
          </PaymentSectionWrapper>
        );
      } else return null;
    } else {
      return (
        <Fragment>
          <PaymentDetailsTitle>
            {t('RESERVATION_APPROVED_PAYMENT_DETAILS_TITLE')}
          </PaymentDetailsTitle>
          <PaymentDetailsAlert>
            {isZeroPrice
              ? t('RESERVATION_ZERO_PAYMENT')
              : t('RESERVATION_PAYMENT_BY_CASH')}
          </PaymentDetailsAlert>
        </Fragment>
      );
    }
  };

  const renderNotificationAlert = () => {
    const attrs = {
      status_code,
      action_required,
      reservation,
      dateLeft,
      expired
    };
    return <NotificationAlert {...attrs} />;
  };

  const renderAdditionalActions = () => {
    return (
      <ActionSection>
        <ActionTitle>{t('NEED_HELP')}</ActionTitle>
        <Actions>
        <ContactInfo>
          <ContactInfoTitle>{t("MARINA_CONTACT_INFO") + ':'}</ContactInfoTitle>
          <ContactDetails>
            <div>
              <MailFilled />
              <ContactDetail>{t("EMAIL_PLACEHOLDER")+ ':'}</ContactDetail>
              <StyledButton
                medium
                variant='link'
                label={marina.mail}
                onClick={() => window.open('mailto:' + marina.mail, '_blank')}
              />
            </div>
            <div>
            <PhoneFilled />
            <ContactDetail>{ t("PHONE")+':'} </ContactDetail>
            <ContactDetail phone onClick={ () => window.location.href = `tel:${phone}` }>{phone }</ContactDetail>

            </div>

          </ContactDetails>
        </ContactInfo>
          <StyledButton
            medium
            variant='link'
            label={t('TECHNICAL_SUPORT')}
            onClick={()=> window.open('mailto:support@pickapier.com', '_blank')}
          />
        </Actions>
      </ActionSection>
    )
  };

  return (
    <ReservationWrapper>
      {width > 767 ? (
        <Container className='reservation-page-container' fullWidth={true}>
          <Row gutter={32}>
            <Col sm={24} md={15}>
              <Tabs
                activeKey={activeTab}
                animated={false}
                renderTabBar={renderTabBar}
                tabBarGutter={0}
                onChange={onTabChange}
              >
                <TabPane tab={t('SUBMISSION_TAB')} key={SUBMISSION_TAB_KEY}>
                  {payment_accepted_online &&
                  status_code === 'UPDATED' &&
                  action_required === 'BOATER' &&
                  !isZeroPrice &&
                  !expired ? (
                    <Fragment>
                      {renderNotificationAlert()}
                      <CollapseWrapper>
                        <Collapse
                          expandIconPosition='right'
                          // onChange={onChangeCollapse}
                          // activeKey={activePanels}
                          bordered={false}
                          ghost={true}
                          defaultActiveKey={defaultActiveKey}
                        >
                          <Panel
                            key='1'
                            className='collapse-panel'
                            header={
                              <SectionHeader>
                                <>
                                  {/* <ReactSVG className="wheel-icon" src={wheelWhiteIcon} /> */}
                                  <span className='section-number'>{'1'}</span>{' '}
                                  {t('RESERVATION_COMPLETE_INFO_HEADER')}
                                </>
                              </SectionHeader>
                            }
                          >
                            {renderSubmission()}
                          </Panel>
                          <Panel
                            key='2'
                            header={
                              <SectionHeader>
                                <>
                                  {' '}
                                  <span className='section-number'>
                                    {'2'}
                                  </span>{' '}
                                  {t('RESERVATION_PAYMENT_INFO_HEADER')}{' '}
                                </>
                              </SectionHeader>
                            }
                          >
                            {renderPaymentForm()}
                          </Panel>
                        </Collapse>
                      </CollapseWrapper>
                    </Fragment>
                  ) : (
                    <Fragment>
                      {renderNotificationAlert()}
                      {renderSubmission()}
                      {renderPaymentDetails()}
                    </Fragment>
                  )}
                </TabPane>

                <TabPane tab={t('CHAT_TAB')} key={CHAT_TAB_KEY}>
                  {renderMessages()}
                </TabPane>
              </Tabs>
            </Col>
            <Col sm={24} md={9}>
              <Sidebar>
                <SidebarScrollOuter>
                  <SidebarScrollInner id='asideScrollContainer'>
                    {renderReservationDetails()}
                  </SidebarScrollInner>

                </SidebarScrollOuter>
              </Sidebar>
              {renderAdditionalActions()}
            </Col>
          </Row>
        </Container>
      ) : (
        <ReservationDetailsMobileWrapper>
          {payment_accepted_online &&
          status_code === 'UPDATED' &&
          action_required === 'BOATER' &&
          !expired ? (
            <Fragment>
              {renderNotificationAlert()}
              {renderReservationDetails()}
              {renderAdditionalActions()}
              <CollapseWrapper>
                <Collapse
                  expandIconPosition='right'
                  bordered={false}
                  ghost={true}
                  defaultActiveKey={defaultActiveKey}
                >
                  <Panel
                    key='1'
                    className='collapse-panel'
                    header={
                      <SectionHeader>
                        <span className='section-number'>{'1'}</span>{' '}
                        {t('RESERVATION_COMPLETE_INFO_HEADER')}
                      </SectionHeader>
                    }
                  >
                    {renderSubmission(true)}
                  </Panel>

                  <Panel
                    key='2'
                    header={
                      <SectionHeader>
                        <>
                          {' '}
                          <span className='section-number'>{'2'}</span>{' '}
                          {t('RESERVATION_PAYMENT_INFO_HEADER')}{' '}
                        </>
                      </SectionHeader>
                    }
                  >
                    {renderPaymentForm()}
                  </Panel>
                </Collapse>
              </CollapseWrapper>
            </Fragment>
          ) : (
            <Fragment>
              {renderNotificationAlert()}
              {renderReservationDetails()}
              {renderAdditionalActions()}
              {renderSubmission()}
              {renderPaymentDetails()}
            </Fragment>
          )}
          {renderReservationActions()}
          <Drawer
            className='messaging-drawer'
            placement='right'
            visible={messageDrawerOpened}
            onClose={onCloseMessageDrawer}
            width='767px'
            zIndex={999998}
            closable={false}
          >
            <MessagingHeader>
              <Button
                className='back_button'
                htmlType='button'
                onClick={onCloseMessageDrawer}
                type='link'
              >
                <FiArrowLeft />
                <BackButtonTitle>{marina.name}</BackButtonTitle>
              </Button>
            </MessagingHeader>
            {renderMessages()}
          </Drawer>
        </ReservationDetailsMobileWrapper>
      )}

      <ConfirmModal
        visible={confirmOpened}
        title={t('CANCEL_RESERVATION_APPROVAL_REQUIRED')}
        confirmButtonTitle={t('CANCEL_RESERVATION')}
        cancelButtonTitle={t('BACK')}
        onConfirm={onCancelReservation}
        asyncConfirm={true}
        onCancel={() => {
          setConfirmOpened(false);
          setReason('');
          setReasonDescription('');
          setEmptyReasonError('');
          setIsOtherReasonSelected(false);
        }}
      >
        {t('ARE_YOU_SURE_WANT_TO_CANCEL_RESERVATION')}
        <ReasonField>
          <Select
            placeholder='Select'
            onChange={val => {
              setIsOtherReasonSelected(val === t('OTHER'));
              setReason(val);
            }}
          >
            {cancellationOptions.map(option => (
              <Option key={option} value={t(option)}>
                {t(option)}
              </Option>
            ))}
          </Select>
          {isOtherReasonSelected && (
            <Input
              placeholder={t('RESERVATION_CANCELATION_REASON_PLACEHOLDER')}
              onChange={e => setReasonDescription(e.target.value)}
              value={reasonDescription}
            />
          )}
          {emptyReasonError && (
            <span className='reason-field-error'>{emptyReasonError}</span>
          )}
        </ReasonField>
      </ConfirmModal>

      <ResultDialog
        className='success-profile-update-result'
        visible={cancelFinished}
        closable={false}
        title={t('CANCELLATION_REQUEST_TITLE')}
        actions={
          <SuccessDialogActions>
            <AsyncButton
              asyncAction={onCloseafterCancel}
              htmlType='button'
              type='primary'
              label={t('OK_BUTTON')}
            />
          </SuccessDialogActions>
        }
      />


      <ConfirmModal
        visible={beforeConfirmOpened}
        title={t('CANCEL_RESERVATION')}
        confirmButtonTitle={t('CONTINUE_CANCEL_RESERVATION')}
        cancelButtonTitle={t('BACK')}
        onConfirm={() => {setBeforeConfirmOpened(false); setConfirmOpened(true);}}
        asyncConfirm={true}
        onCancel={() => setBeforeConfirmOpened(false)}
      >
        {sanitizeTranslation(marinaLogos(marina.id)? t('MARINA_LOGOS_CANCEL_RESERVATION'): '' )}
        {t('GENERAL_CANCEL_RESERVATION')}
        <CANCELLATION_POLICY onClick={ ()=> {
          if(cancellationPolicy && cancellationPolicy.url){
            window.open(cancellationPolicy.url);
          }
        }}> {t('CANCELLATION_POLICY') + '.'} </CANCELLATION_POLICY>
        {t('GENERAL_CANCEL_RESERVATION_2')}
      </ConfirmModal>

      <ResultDialog
        className='info-required-info-missing-result'
        visible={infoModalOpened}
        status='info'
        title={t('REQUIRED_DOCUMENTS_ARE_MISSING')}
        subtitle={t('YOU_HAVE_TO_ADD_REQUIRED_DOCUMENTS')}
        onCancel={async () => {
          await loadAllMarinas();
          setInfoModalOpened(false);
        }}
      />

      <Spinner spinning={showSpinner} tip={t('SIGNING_UP_SPINNER_TEXT')} />
    </ReservationWrapper>
  );
}

const ReservationPage = props => {
  const { user } = useContext(AuthContext);
  return (
    <Elements
      stripe={stripePromise}
      options={stripeElementsOptions(user)}
    >
      <RenderReservationPage {...props}/>
    </Elements>
  )
}

export default ReservationPage;
